import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { useAuth } from '../contexts/AuthContext';
import { canAccessInternalPages } from '../utils/accessControl';
import { accessPointDirectionScope, directionLabel } from '../utils/accessPointDirections';
import { flagError } from '../utils/flagSystem';
import accessPointDirectionsApi from '../api/accessPointDirectionsApi';

const options = [{ label: 'Entry', value: 'Entry' }, { label: 'Exit', value: 'Exit' }];
const errorText = (error, fallback) => !error.response && !error.isAxiosError && error.message ?
  error.message : flagError(error, fallback);

const DirectionEditor = ({ scope, onHide, onLoaded, onAccessError }) => {
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState(null);
  const [latest, setLatest] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const mounted = useRef(true);
  const busy = useRef(false);
  const toast = useRef(null);

  useEffect(() => {
    mounted.current = true;
    load(false);
    return () => { mounted.current = false; };
    // The parent remounts this editor for each authenticated route/access-point scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async reconcile => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true); setFeedback(null); setConfirming(false); setLatest(null);
    try {
      const records = await accessPointDirectionsApi.list(scope.customerId, scope.locationId);
      if (!mounted.current) return;
      const record = records.find(item => item.accessPointId === scope.accessPointId);
      if (!record) throw new Error('This access point is no longer available in the selected customer/location. Close this dialog and refresh access points.');
      if (reconcile) setLatest(record);
      else {
        setCurrent(record); setSelected(record.direction);
        onLoaded(record);
      }
    } catch (error) {
      if (!mounted.current || onAccessError(error)) return;
      setFeedback({ severity: 'error', text: errorText(error, 'Could not load the access-point direction.') });
    } finally {
      if (mounted.current) { busy.current = false; setLoading(false); }
    }
  };

  const save = async () => {
    if (busy.current || conflict || !current || !['Entry', 'Exit'].includes(selected) || selected === current.direction) return;
    if (current.direction !== null && !confirming) { setConfirming(true); setFeedback(null); return; }
    busy.current = true; setSaving(true); setFeedback(null);
    try {
      const record = await accessPointDirectionsApi.save(scope.customerId, scope.locationId, scope.accessPointId,
        { direction: selected, revision: current.revision });
      if (!mounted.current) return;
      setCurrent(record); setSelected(record.direction); setConfirming(false);
      onLoaded(record);
      const text = `Direction saved as ${record.direction}.`;
      setFeedback({ severity: 'success', text });
      if (toast.current) toast.current.show({ severity: 'success', summary: 'Successful', detail: text, life: 3000 });
    } catch (error) {
      if (!mounted.current || onAccessError(error)) return;
      const status = error.response && error.response.status;
      const needsReload = !status || status === 409 || status === 404 || status >= 500;
      setConflict(needsReload); setConfirming(false);
      setFeedback({ severity: 'error', text: status === 409 ?
        'This direction changed concurrently. Your selection has been preserved. Reload the latest direction and explicitly review its revision before saving again.' :
        `${errorText(error, 'Could not save the direction.')}${needsReload ? ' Reload and review the latest direction before another save.' : ''}` });
    } finally {
      if (mounted.current) { busy.current = false; setSaving(false); }
    }
  };
  const acceptRevision = () => {
    setCurrent(latest); onLoaded(latest); setLatest(null); setConflict(false);
    setFeedback({ severity: 'info', text: 'Latest revision accepted. Your selection is unchanged; review it and save again if a change is still needed.' });
  };
  const unavailable = loading || saving;
  return <Dialog header="Entry/Exit Direction" visible modal draggable={false}
    style={{ width: '34rem', maxWidth: '95vw' }} onHide={saving ? undefined : onHide}
    footer={<>
      <Button label="Close" className="p-button-text" onClick={onHide} disabled={saving} />
      {confirming && <Button label="Cancel Change" className="p-button-text" onClick={() => setConfirming(false)} disabled={saving} />}
      <Button label={confirming ? 'Confirm Direction Change' : 'Save Direction'} icon="pi pi-save" onClick={save}
        loading={saving} disabled={unavailable || conflict || !current || !selected || selected === current.direction} />
    </>}>
    <Toast ref={toast} />
    <p>Customer {scope.customerId}, location {scope.locationId}, access point {scope.accessPointId}.</p>
    {feedback && <div className="mb-3"><Message severity={feedback.severity} text={feedback.text} /></div>}
    {loading && <ProgressBar mode="indeterminate" style={{ height: '6px' }} />}
    {!current && !loading && <Button label="Retry Direction Load" onClick={() => load(false)} />}
    {current && <div className="p-fluid">
      <p><b>Tile:</b> {current.tile || '\u2014'}<br /><b>Gate:</b> {current.gate || '\u2014'}</p>
      <p>Saved direction: <b>{directionLabel(current.direction)}</b> (revision {current.revision})</p>
      <label className="d-block mb-2" htmlFor={`direction-${scope.accessPointId}`}>Entry/Exit</label>
      <Dropdown inputId={`direction-${scope.accessPointId}`} value={selected} options={options}
        placeholder="Choose Entry or Exit" disabled={unavailable || confirming}
        onChange={event => { setSelected(event.value); setFeedback(null); }} />
      <p className="mt-2 text-muted">Direction is explicitly configured here; tile/gate names and LMS port type do not determine it.</p>
    </div>}
    {confirming && <Message severity="warn" text={`Change ${current.direction} to ${selected}? Changing an already configured direction invalidates applicable overrides tied to the earlier direction revision.`} />}
    {conflict && <div className="mt-3">
      <Button label="Reload Latest Direction" className="p-button-text" disabled={unavailable} onClick={() => load(true)} />
      {latest && <>
        <p>Latest saved direction: <b>{directionLabel(latest.direction)}</b> (revision {latest.revision}).
          Your selected direction remains <b>{selected}</b>.</p>
        <Button label="Use Latest Revision" disabled={unavailable} onClick={acceptRevision} />
      </>}
    </div>}
  </Dialog>;
};

const ScopedDirectionControl = ({ scope, onAccessError, listState, onRetry }) => {
  const [open, setOpen] = useState(false);
  const [record, setRecord] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    if (listState) setRecord(listState.record || null);
  }, [listState && listState.record, listState && listState.loading, listState && listState.error]);
  const handleError = error => {
    if (!onAccessError(error)) return false;
    setOpen(false); setRecord(null); setDenied(true);
    return true;
  };
  if (denied) return <Message severity="error" text="Direction configuration access is unavailable." />;
  return <>
    {listState && listState.loading ? <span role="status"><i className="pi pi-spin pi-spinner mr-2" aria-hidden="true" />Loading direction...</span> :
      listState && listState.error ? <div><Message severity="error" text={listState.error} />
        <Button label="Retry Directions" className="p-button-text p-button-sm" onClick={onRetry} /></div> :
        <Button label={record ? directionLabel(record.direction) : 'Configure Entry/Exit'} icon="pi pi-sort-alt"
          className="p-button-text p-button-sm" onClick={() => setOpen(true)} />}
    {open && <DirectionEditor scope={scope} onHide={() => setOpen(false)} onLoaded={setRecord} onAccessError={handleError} />}
  </>;
};

const AccessPointDirectionControl = ({ accessPoint, routeContext, onAccessError, listState, onRetry }) => {
  const { accessContext, isAuthReady, isLoggedIn } = useAuth();
  if (!isAuthReady || !isLoggedIn || !canAccessInternalPages(accessContext)) return null;
  const scope = accessPointDirectionScope(accessPoint, routeContext);
  if (!scope) return <span className="text-muted">Customer/location context unavailable</span>;
  const key = JSON.stringify([localStorage.getItem('token'), accessContext, routeContext.type, routeContext.typeId, scope]);
  return <ScopedDirectionControl key={key} scope={scope} onAccessError={onAccessError} listState={listState} onRetry={onRetry} />;
};
export default AccessPointDirectionControl;
